import { useEffect, useRef, useState } from "react";
import { Link, router, usePage } from "@inertiajs/react";
import { ListChecks, Settings } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageTitle from "@/components/PageTitle";
import TaskRow from "@/pages/Tasks/components/TaskRow";
import TaskDrawer from "@/pages/Tasks/components/TaskDrawer";
import type { Person } from "@/pages/Tasks/task";
import PauseDialog from "@/pages/Tasks/components/PauseDialog";
import { settings_task_settings_path, snooze_task_path } from "@/lib/routes";
import { replaceUrl } from "@/lib/replaceUrl";
import {
  type Task, type Zone, zoneFor, attentionOrder, dueDays, workingDue, shortDateLabel, taskIdFromUrl,
} from "@/pages/Tasks/task";

interface Props {
  tasks: Task[];
  people: Person[];
}

const SECTION = "flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";
// No overflow-hidden: the selected row's pointer chevron sits OUTSIDE the
// card, in the gap toward the details panel. Rows round their own corners.
const CARD = "divide-y divide-border rounded-lg border border-border bg-background shadow-sm";

// My tasks (final UX direction): two zones only — Needs attention (overdue ∪
// due today ∪ sent-back ∪ publish-blocking) and Coming up (every future task).
// Paused work is one quiet footer sentence, expandable. Completed tasks get
// their own view later; this page never lists them. The page says "N things,
// this one first" — never "you have a backlog".
export default function Index({ tasks, people }: Props) {
  const { props: pageProps } = usePage<{ can_manage_notification_policy?: boolean; [key: string]: unknown }>();
  const canManageTaskSettings = Boolean(pageProps.can_manage_notification_policy);
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    typeof window === "undefined" ? null : taskIdFromUrl(window.location.href)
  );
  const [pauseTaskId, setPauseTaskId] = useState<string | null>(null);
  const [showPaused, setShowPaused] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  // Inertia reuses this mounted component for a same-page visit (e.g. a bell
  // notification redirecting back to /tasks?task=<id>), so the lazy useState
  // initializer above only covers the cold-load case.
  useEffect(() => {
    return router.on("success", (event) => setSelectedId(taskIdFromUrl(event.detail.page.url)));
  }, []);

  const zones: Record<Zone, Task[]> = { attention: [], upcoming: [], paused: [], done: [] };
  tasks.forEach((task) => zones[zoneFor(task)].push(task));
  zones.attention.sort(attentionOrder);
  zones.upcoming.sort((a, b) => dueDays(workingDue(a)) - dueDays(workingDue(b)));
  const overdueCount = zones.attention.filter((t) => dueDays(workingDue(t)) < 0).length;
  const todayCount = zones.attention.filter((t) => dueDays(workingDue(t)) === 0).length;

  // Plan name as disambiguation only when the same title appears twice in
  // the open lists — never alongside a why-phrase, often not at all.
  const titleCounts = new Map<string, number>();
  [...zones.attention, ...zones.upcoming].forEach((t) => titleCounts.set(t.details, (titleCounts.get(t.details) ?? 0) + 1));
  const duplicated = (task: Task) => (titleCounts.get(task.details) ?? 0) > 1;

  const selectedTask = selectedId ? tasks.find((task) => task.id === selectedId) ?? null : null;
  const pauseTask = pauseTaskId ? tasks.find((task) => task.id === pauseTaskId) ?? null : null;

  const openDetails = (task: Task) => {
    setSelectedId(task.id);
    const url = new URL(window.location.href);
    url.searchParams.set("task", task.id);
    replaceUrl(url, {});
  };

  const closeDrawer = () => {
    setSelectedId(null);
    const url = new URL(window.location.href);
    if (url.searchParams.has("task")) {
      url.searchParams.delete("task");
      replaceUrl(url, {});
    }
  };

  // ↑↓ walk the rows; Enter opens the work page, Space opens details (both
  // handled on the row itself).
  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const rows = Array.from(listRef.current?.querySelectorAll<HTMLElement>("[data-task-row]") ?? []);
    if (rows.length === 0) return;
    const index = rows.findIndex((row) => row === document.activeElement);
    e.preventDefault();
    const next = e.key === "ArrowDown" ? Math.min(index + 1, rows.length - 1) : Math.max(index - 1, 0);
    rows[next]?.focus();
  };

  const rowProps = (task: Task) => ({
    task,
    showPlanName: duplicated(task),
    selected: task.id === selectedId,
    onOpenDetails: () => openDetails(task),
    onPause: () => setPauseTaskId(task.id),
  });

  const resume = (task: Task) => {
    router.delete(snooze_task_path(task.id), { preserveScroll: true });
  };

  const headerFragments = [
    overdueCount > 0 && <em key="o" className="not-italic font-semibold text-destructive">{overdueCount} overdue</em>,
    todayCount > 0 && <b key="t" className="font-semibold text-warning">{todayCount} due today</b>,
    zones.upcoming.length > 0 && <span key="u">{zones.upcoming.length} coming up</span>,
  ].filter(Boolean) as React.ReactNode[];

  if (tasks.length === 0) {
    return (
      <AppLayout flush header={<PageTitle title="My tasks" />}>
        <div className="flex flex-col px-6 py-5">
        {canManageTaskSettings && (
          <div className="flex justify-start">
            <Link
              href={settings_task_settings_path()}
              title="Task Settings"
              aria-label="Task Settings"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Settings size={14} />
              Task Settings
            </Link>
          </div>
        )}
        <div className="flex flex-col items-center py-16 text-center">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ListChecks size={22} />
          </span>
          <h3 className="text-base font-semibold text-foreground">No tasks right now</h3>
          <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-muted-foreground">
            You have no tasks currently. Your tasks are shown here after they are created and assigned to you.
          </p>
        </div>
        </div>
      </AppLayout>
    );
  }

  return (
    // The page scrolls as one; the details panel stays pinned beside the list.
    <AppLayout flush header={<PageTitle title="My Work" subtitle={"Manage your work in Atlas"} />}>
      <div className="flex items-start gap-1 px-6 py-5">
      <div className="flex min-w-0 flex-col lg:w-3/5">
        {canManageTaskSettings && (
          <div className="mb-3 flex justify-start">
            <Link
              href={settings_task_settings_path()}
              title="Task Settings"
              aria-label="Task Settings"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Settings size={14} />
              Task Settings
            </Link>
          </div>
        )}
        <div className="flex flex-col pr-4">
        <div ref={listRef} onKeyDown={onListKeyDown} className="flex flex-col">
          {zones.attention.length === 0 ? null : (
            <div>
              <div className="mb-6">
                <p className="text-lg font-semibold tracking-tight text-foreground">
                  {zones.attention.length} thing{zones.attention.length === 1 ? " needs" : "s need"} your attention
                </p>
                {headerFragments.length > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {headerFragments.map((fragment, i) => (
                      <span key={i}>
                        {i > 0 && " · "}
                        {fragment}
                      </span>
                    ))}
                  </p>
                )}
              </div>

              <p className={SECTION}>
                <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                Needs attention <span className="normal-case tracking-normal">· {zones.attention.length}</span>
              </p>
              <div className={`mt-2 ${CARD}`}>
                {zones.attention.map((task) => (
                  <TaskRow key={task.id} {...rowProps(task)} />
                ))}
              </div>
            </div>
          )}

          {zones.upcoming.length > 0 && (
            <>
              <p className={`${zones.attention.length === 0 ? "" : "mt-7"} ${SECTION}`}>
                Coming up{" "}
                <span className="normal-case tracking-normal">· {zones.upcoming.length}</span>
              </p>
              <div className={`mt-2 ${CARD}`}>
                {zones.upcoming.map((task) => (
                  <TaskRow key={task.id} {...rowProps(task)} />
                ))}
              </div>
            </>
          )}
        </div>

        {zones.paused.length > 0 && (
          <div className="mt-5 flex flex-col gap-2">
            <p className={`text-xs text-muted-foreground ${zones.attention.length === 0 ? "text-center" : ""}`}>
              <button
                type="button"
                onClick={() => setShowPaused((v) => !v)}
                className="underline-offset-[3px] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
              >
                {zones.paused.length} paused
              </button>
            </p>

            {showPaused && (
              <div className={CARD}>
                {zones.paused.map((task) => (
                  <div key={task.id} className="flex min-h-[48px] items-center gap-5 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-muted-foreground">{task.details}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {`Reminders paused${task.snoozed_until ? ` until ${shortDateLabel(task.snoozed_until)}` : ""} · ${task.snoozed_by_me ? "by you" : `by ${task.snoozed_by_name ?? "someone"}`}${task.snooze_reason ? ` · “${task.snooze_reason}”` : ""}`}
                      </p>
                    </div>
                    {task.can.resume && (
                      <button
                        type="button"
                        onClick={() => resume(task)}
                        className="inline-flex h-8 shrink-0 items-center rounded-md border border-border bg-background px-3.5 text-xs font-semibold text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        Resume
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      </div>

      {/* Inline detail panel — part of the page, not an overlay. Sized to fit
          below the title on load, then pinned while the list scrolls. */}
      {selectedTask && (
        <div
          className="sticky top-5 hidden min-w-0 flex-1 lg:block"
          style={{ height: "calc(var(--pane-height, 100vh) - var(--title-band-height, 0px) - 40px)" }}
        >
          <TaskDrawer task={selectedTask} people={people} onClose={closeDrawer} />
        </div>
      )}
      </div>
      {pauseTask && <PauseDialog task={pauseTask} open onClose={() => setPauseTaskId(null)} />}
    </AppLayout>
  );
}
